import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createJob } from '../api';
import FileUpload from '../components/FileUpload';
import ModeSelector from '../components/ModeSelector';
import LevelSelector from '../components/LevelSelector';
import EstimatePanel from '../components/EstimatePanel';
import AdvancedOptions from '../components/AdvancedOptions';
import '../styles/components/buttons.css';
import '../styles/components/forms.css';

const SERPER_PLANS = [
  { key: '50k',   label: '50K credits — $50 ($1.00/1k)' },
  { key: '500k',  label: '500K credits — $375 ($0.75/1k)' },
  { key: '2.5m',  label: '2.5M credits — $1,250 ($0.50/1k)' },
  { key: '12.5m', label: '12.5M credits — $3,750 ($0.30/1k)' },
];

const SERPAPI_PLANS = [
  { key: 'starter',    label: 'Starter — 1K — $25 ($25.00/1k)' },
  { key: 'developer',  label: 'Developer — 5K — $75 ($15.00/1k)' },
  { key: 'production', label: 'Production — 15K — $150 ($10.00/1k)' },
  { key: 'big_data',   label: 'Big Data — 30K — $275 ($9.17/1k)' },
  { key: 'searcher',   label: 'Searcher — 100K — $725 ($7.25/1k)' },
  { key: 'volume',     label: 'Volume — 250K — $1,475 ($5.90/1k)' },
  { key: 'infra',      label: 'Infrastructure — 500K — $2,750 ($5.50/1k)' },
  { key: 'cloud_1m',   label: 'Cloud 1M — $3,750 ($3.75/1k)' },
  { key: 'cloud_2m',   label: 'Cloud 2M — $5,675 ($2.84/1k)' },
  { key: 'cloud_3m',   label: 'Cloud 3M — $7,600 ($2.53/1k)' },
  { key: 'cloud_4m',   label: 'Cloud 4M — $9,550 ($2.39/1k)' },
];

export default function NewJobPage() {
  const [fileData, setFileData] = useState(null);
  const [mode, setMode] = useState('age_estimation');
  const [level, setLevel] = useState(1);
  const [advFlags, setAdvFlags] = useState({ linkedin_engine: 'apify' });
  const [serperPlan, _setSerperPlan] = useState(() => localStorage.getItem('serperPlan') || '50k');
  const [serpapiPlan, _setSerpapiPlan] = useState(() => localStorage.getItem('serpapiPlan') || 'production');
  const setSerperPlan = (v) => { localStorage.setItem('serperPlan', v); _setSerperPlan(v); };
  const setSerpapiPlan = (v) => { localStorage.setItem('serpapiPlan', v); _setSerpapiPlan(v); };
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [fileStats, setFileStats] = useState(null);
  const navigate = useNavigate();

  const PIPELINE_KEYS = ['output_validation', 'skip_linkedin', 'skip_website'];

  const clearPipelineFlags = (prev) => {
    const next = { ...prev };
    PIPELINE_KEYS.forEach(k => { next[k] = null; });
    return next;
  };

  const handleModeChange = (m) => {
    setMode(m);
    setLevel(1);
    setAdvFlags(clearPipelineFlags);
  };

  const handleLevelChange = (l) => {
    setLevel(l);
    setAdvFlags(clearPipelineFlags);
  };

  const handleSubmit = async () => {
    if (!fileData) return;
    setSubmitting(true);
    try {
      const job = await createJob(fileData.file, {
        mode, level, ...advFlags,
        ...(mode === 'discovery' && { serper_plan: serperPlan, serpapi_plan: serpapiPlan }),
      });
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="page-enter">
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.6px' }}>
        New Job
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 36 }}>
        Upload your file and configure the processing pipeline
      </p>

      <FileUpload file={fileData} onFileChange={setFileData} mode={mode} onFileStats={setFileStats} />

      <div className="section-label">Mode</div>
      <ModeSelector mode={mode} onModeChange={handleModeChange} />

      <div className="section-label">Level</div>
      <LevelSelector mode={mode} level={level} onLevelChange={handleLevelChange} />

      <div className="options-row" style={{ marginBottom: 24 }}>
        <div className="option-group">
          <div className="option-label">Start Row</div>
          <input
            type="number"
            className="option-input"
            placeholder="Auto"
            min="1"
            value={advFlags.start || ''}
            onChange={(e) => setAdvFlags(prev => ({ ...prev, start: e.target.value ? parseInt(e.target.value) : null }))}
          />
        </div>
        <div className="option-group">
          <div className="option-label">Row Limit</div>
          <input
            type="number"
            className="option-input"
            placeholder="All"
            min="1"
            value={advFlags.limit || ''}
            onChange={(e) => setAdvFlags(prev => ({ ...prev, limit: e.target.value ? parseInt(e.target.value) : null }))}
          />
        </div>
        <div className="option-group">
          <div className="option-label">&nbsp;</div>
          <div className="checkbox-row" style={{ margin: 0, height: 40 }}>
            <input type="checkbox" id="opt-fresh" checked={!!advFlags.fresh} onChange={(e) => setAdvFlags(prev => ({ ...prev, fresh: e.target.checked }))} />
            <label htmlFor="opt-fresh">Start fresh</label>
          </div>
        </div>
      </div>

      {mode === 'discovery' && (
        <div style={{ marginBottom: 24 }}>
          <div className="option-label" style={{ marginBottom: 8, fontWeight: 600, color: 'var(--text2)' }}>Pricing Plans for Discovery Cost Estimate</div>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 12 }} />
          <div className="options-row">
          <div className="option-group" style={{ flex: '1 1 0' }}>
            <div className="option-label">Serper Plan</div>
            <select className="option-select" style={{ width: '100%' }} value={serperPlan} onChange={(e) => setSerperPlan(e.target.value)}>
              {SERPER_PLANS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="option-group" style={{ flex: '1 1 0' }}>
            <div className="option-label">SerpAPI Plan</div>
            <select className="option-select" style={{ width: '100%' }} value={serpapiPlan} onChange={(e) => setSerpapiPlan(e.target.value)}>
              {SERPAPI_PLANS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          </div>
        </div>
      )}

      <EstimatePanel
        rowCount={(() => {
          let count = mode === 'discovery' && fileData?.emptyLinkedIn != null ? fileData.emptyLinkedIn : fileData?.rowCount;
          // Use remaining rows if file has been partially processed
          if (count && fileStats && fileStats.processed > 0) {
            count = Math.max(count - fileStats.processed, 0);
          }
          if (count && advFlags.limit && advFlags.limit > 0) count = Math.min(count, advFlags.limit);
          return count;
        })()}
        totalRows={fileData?.rowCount}
        mode={mode}
        level={level}
        linkedinEngine={advFlags.linkedin_engine || 'apify'}
        overrides={{
          ...(advFlags.output_validation != null && { output_validation: advFlags.output_validation }),
          ...(advFlags.skip_linkedin != null && { skip_linkedin: advFlags.skip_linkedin }),
          ...(advFlags.skip_website != null && { skip_website: advFlags.skip_website }),
        }}
        serperPlan={serperPlan}
        serpapiPlan={serpapiPlan}
      />

      {mode !== 'discovery' && (
        <AdvancedOptions flags={advFlags} onChange={setAdvFlags} mode={mode} level={level} />
      )}

      <div style={{ marginTop: 32 }}>
        <button
          className="btn btn-primary"
          disabled={!fileData || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Starting...' : 'Start Processing'}
        </button>
      </div>
    </div>
  );
}
